import React, { FC, useRef } from 'react';
import './PageHeaderLayout.css';
import { useBreakPoint, Logo, IoPersonCircle } from '@/Shared';
import { UserProfile, useUserProfile } from '@/Domains';
import Dropdown from '../../Components/Dropdown/Dropdown';

interface PageHeaderLayoutProps { }

/**
 * =====================================================
 * PageHeaderLayout
 * =====================================================
 *
 * Header layout component displayed at the top of the application.
 *
 * Responsibilities:
 * - Displays the application logo
 * - Shows the authenticated user's identity
 * - Provides access to the user profile via a dropdown menu
 * - Adapts its rendering based on screen size (mobile / desktop)
 *
 * This component is purely presentational:
 * - State and business logic are delegated to dedicated hooks
 * - UI behavior is driven by responsive and session state
 *
 * Author: Samano CASTRE
 * Date: July 16, 2025
 */
const PageHeaderLayout: FC<PageHeaderLayoutProps> = () => {

	/**
	 * =====================================================
	 * User profile state
	 * =====================================================
	 *
	 * Retrieves the current user view model and controls
	 * the dropdown visibility logic.
	 */
	const {
		viewModel,
		isDropdownOpen,
		toggleDropdown,
		closeDropdown,
	} = useUserProfile();

	/**
	 * =====================================================
	 * Responsive context
	 * =====================================================
	 *
	 * Used to adapt UI elements depending on screen size.
	 */
	const { isMobile } = useBreakPoint();


	/**
	 * =====================================================
	 * Dropdown trigger reference
	 * =====================================================
	 *
	 * Shared reference between the trigger element and the
	 * dropdown component to manage positioning and outside clicks.
	 */
	const triggerRef = useRef<HTMLDivElement>(null);


	/**
	 * =====================================================
	 * Render
	 * =====================================================
	 *
	 * Layout structure:
	 * - Application logo
	 * - User identity trigger (icon + name on desktop)
	 * - Dropdown containing the user profile panel
	 */
	return (
		<div className="MobileHeaderLayout elevation-1" data-testid="MobileHeaderLayout">
			<Logo />

			{viewModel && (
				<div className="PageHeaderLayout__session">
					<div
						ref={triggerRef}
						className="PageHeaderLayout__user"
						onClick={toggleDropdown}
					>
						<IoPersonCircle className="user__icon" />
						{!isMobile && <span className="user__name">{viewModel.fullName}</span>}
					</div>
					<Dropdown
						triggerRef={triggerRef}
						isOpen={isDropdownOpen}
						onClose={closeDropdown}
						content={
							<div className='w-18 h-20'>
								<UserProfile onClose ={closeDropdown}/>
							</div>
						}
					/>
				</div>
			)}
		</div>
	);
};

export default PageHeaderLayout;
